import { GitHubIcon, LinkedInIcon } from "@/aptim/aptim";
import { useFormAction, useNavigation } from "@remix-run/react";
import { useState } from "react";
import userImg from "~/img/user.png";

export const pricingTiers = [
  {
    name: "Starter",
    id: "tier-starter",
    ctaText: "Subscribe",
    href: "/signup?tier=starter",
    priceMonthly: "£15",
    pricePeriod: "/month",
    description: "Ideal for small teams starting with data management",
    features: [
      "1 organisation",
      "Basic visualisation for key metrics",
      "Up to 10GB of data storage",
      "Email based support",
    ],
    mostPopular: false,
  },
  {
    name: "Professional",
    id: "tier-professional",
    ctaText: "Subscribe",
    href: "/signup?tier=professional",
    priceMonthly: "£50",
    pricePeriod: "/month",
    description: "Advanced features for growing teams.",
    features: [
      "All starter tier features",
      "5 organisations",
      "Up to 30GB of data storage",
      "Priority support via email",
    ],
    mostPopular: true,
  },
  {
    name: "Enterprise",
    id: "tier-enterprise",
    ctaText: "Inquire",
    href: "/signup?tier=enterprise",
    priceMonthly: "£200",
    pricePeriod: "/month",
    description: "For organisations scaling their data operations.",
    features: [
      "All professional tier features",
      "20 organisations",
      "Dedicated onboarding and custom configuration",
      "Up to 100GB of data storage",
      "SLA-backed support with a dedicated account manager",
      "API access for custom applications",
    ],
    mostPopular: false,
  },
];

export const appDetails = {
  version: "0.0.0.1",
  title: "DataDock",
  titleShort: "DataDock",
  tagline: "Simplify. Centralise. Visualise.",
  descriptionText: `DataDock is a standardised data warehouse solution tailored for
      organisations with limited IT resources. Designed to streamline data
      consolidation and visualisation, it empowers you to quickly integrate and
      manage data without the complexity of traditional systems.`,
  description: (
    <p className="font-extralight text-2xl mt-2">
      DataDock is a standardised data warehouse solution tailored for
      organisations with limited IT resources. Designed to streamline data
      consolidation and visualisation, it empowers you to quickly integrate and
      manage data without the complexity of traditional systems.
    </p>
  ),
  privacyLink: "https://aptim-solutions.com/privacy",
  termsLink: "https://aptim-solutions.com/terms-of-service",
  loginLink: "https://datadock.aptim-solutions.com/login",
  signupLink: "https://datadock.aptim-solutions.com/signup",
  supportLink: "https://aptim-solutions.com/support",
  companyName: "APTIM-Solutions",
  companyTagline: "Advancing Performance Through Input Management",
  companyShortName: "APTIM-Solutions",
  socialMedia: [
    {
      title: "LinkedIn",
      href: "https://linkedin.com/company/aptimsolutions",
      icon: LinkedInIcon,
    },
    {
      title: "GitHub",
      href: "https://github.com/aptim-solutions",
      icon: GitHubIcon,
    },
  ],
  companies: [
    {
      legalName: "APTIM-Solutions Limited",
      registrationStatement: "Registered in England and Wales (11976801)",
      address:
        "71-75 Shelton Street, London, Covent Garden, WC2H 9JQ, United Kingdom",
    },
  ],
  companyBlurb: (
    <span className="font-extralight text-2xl mt-2">
      At APTIM, we specialise in advancing performance through strategic input
      management, helping organisations streamline operations, maximise
      resources, and achieve their highest potential. Our innovative solutions
      are designed to optimise every stage of your processes, delivering
      measurable improvements and driving sustainable growth. With APTIM, you
      gain a partner dedicated to precision, efficiency, and excellence in
      performance.
    </span>
  ),
  companyFooter: (
    <div>
      <div className="my-2 border-border border-2" />
      <p className="font-bold">APTIM-Solutions Limited</p>
      <p>
        Registered 11976801 in England and Wales at 71-75 Shelton Street, Covent
        Garden, London, WC2H 9JQ, United Kingdom
      </p>
    </div>
  ),
};

// export function classNames(...classes) {
//   return classes.filter(Boolean).join(" ");
// }

export function getErrorMessage(error: unknown) {
  if (typeof error === "string") return error;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    typeof error.data === "string"
  ) {
    return error.data;
  }
  console.error("Unable to get error message for error", error);
  return "Unknown Error";
}

export function getDomainUrl(request: Request) {
  const host =
    request.headers.get("X-Forwarded-Host") ??
    request.headers.get("host") ??
    new URL(request.url).host;
  const protocol = host.includes("localhost") ? "http" : "https";
  return `${protocol}://${host}`;
}

/**
 * Combine multiple header objects into one (uses append so headers are not overridden)
 */
export function combineHeaders(
  ...headers: Array<ResponseInit["headers"] | null | undefined>
) {
  const combined = new Headers();
  for (const header of headers) {
    if (!header) continue;
    for (const [key, value] of new Headers(header).entries()) {
      combined.append(key, value);
    }
  }
  return combined;
}

/**
 * Returns true if the current navigation is submitting the current route's
 * form. Defaults to the current route's form action and method POST.
 *
 * Defaults state to 'non-idle'
 *
 * NOTE: the default formAction will include query params, but the
 * navigation.formAction will not, so don't use the default formAction if you
 * want to know if a form is submitting without specific query params.
 */
export function useIsPending({
  formAction,
  formMethod = "POST",
  state = "non-idle",
}: {
  formAction?: string;
  formMethod?: "POST" | "GET" | "PUT" | "PATCH" | "DELETE";
  state?: "submitting" | "loading" | "non-idle";
} = {}) {
  const contextualFormAction = useFormAction();
  const navigation = useNavigation();
  const isPendingState =
    state === "non-idle"
      ? navigation.state !== "idle"
      : navigation.state === state;
  return (
    isPendingState &&
    navigation.formAction === (formAction ?? contextualFormAction) &&
    navigation.formMethod === formMethod
  );
}

function callAll<Args extends Array<unknown>>(
  ...fns: Array<((...args: Args) => unknown) | undefined>
) {
  return (...args: Args) => fns.forEach((fn) => fn?.(...args));
}

/**
 * Use this hook with a button and it will make it so the first click sets a
 * `doubleCheck` state to true, and the second click will actually trigger the
 * `onClick` handler. This allows you to have a button that can be like a
 * "are you sure?" experience for the user before doing destructive operations.
 */
export function useDoubleCheck() {
  const [doubleCheck, setDoubleCheck] = useState(false);

  function getButtonProps(
    props?: React.ButtonHTMLAttributes<HTMLButtonElement>
  ) {
    const onBlur: React.ButtonHTMLAttributes<HTMLButtonElement>["onBlur"] =
      () => setDoubleCheck(false);

    const onClick: React.ButtonHTMLAttributes<HTMLButtonElement>["onClick"] =
      doubleCheck
        ? undefined
        : (e) => {
            e.preventDefault();
            setDoubleCheck(true);
          };

    const onKeyUp: React.ButtonHTMLAttributes<HTMLButtonElement>["onKeyUp"] = (
      e
    ) => {
      if (e.key === "Escape") {
        setDoubleCheck(false);
      }
    };

    return {
      ...props,
      onBlur: callAll(onBlur, props?.onBlur),
      onClick: callAll(onClick, props?.onClick),
      onKeyUp: callAll(onKeyUp, props?.onKeyUp),
    };
  }

  return { doubleCheck, getButtonProps };
}

export const getUserIp = async () => {
  const response = await fetch("https://api.ipify.org/?format=json");
  if (!response.ok) {
    console.log("error not ok");
    return "";
  }
  const json = await response.json();
  return json.ip;
};

/**
 * Combine multiple response init objects into one (uses combineHeaders)
 */
export function combineResponseInits(
  ...responseInits: Array<ResponseInit | null | undefined>
) {
  let combined: ResponseInit = {};
  for (const responseInit of responseInits) {
    combined = {
      ...responseInit,
      headers: combineHeaders(combined.headers, responseInit?.headers),
    };
  }
  return combined;
}

export function getUserImgSrc(imageId?: string | null) {
  return imageId ? `/app/resources/user-images/${imageId}` : userImg;
}

export function getOrderBy(sortBy, sortOrder) {
  let orderBy = {};
  if (sortBy.includes(".")) {
    let [fieldOne, fieldTwo] = sortBy.split(".");
    if (typeof fieldOne !== "string" || typeof fieldTwo !== "string") {
      orderBy = {
        [sortBy]: sortOrder,
      };
    } else {
      orderBy = {
        [fieldOne]: {
          [fieldTwo]: sortOrder,
        },
      };
    }
  } else {
    orderBy = {
      [sortBy]: sortOrder,
    };
  }
  return orderBy;
}

export function getSearchQuery(qrySearch: string | null, fields: string[]) {
  if (!qrySearch) {
    return {};
  }

  const searchFilter = { contains: qrySearch, mode: "insensitive" };

  return {
    OR: fields.map((field) => ({ [field]: searchFilter })),
  };
}
